import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type {
  ForgeTokenDetail,
  ForgeTokenDetailResponse,
} from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const FORGE_TOKEN_DETAIL_QUERY_KEY = "wp:forge-token-detail";

async function fetchForgeTokenDetail(
  baseUrl: string,
  tokenAddress: string,
): Promise<ForgeTokenDetail | null> {
  const res = await fetch(`${baseUrl}/v1/tokens/${tokenAddress}`, {
    cache: "no-store",
  });
  if (!res.ok) return null;
  const json: ForgeTokenDetailResponse = await res.json();
  return json?.data?.token ?? null;
}

export function useForgeTokenDetails(
  env: Environment,
  tokenAddresses: string[],
  enabled = true,
) {
  const results = useQueries({
    queries: tokenAddresses.map((addr) => ({
      queryKey: [FORGE_TOKEN_DETAIL_QUERY_KEY, env, addr],
      queryFn: async () =>
        fetchForgeTokenDetail(await getApiBase("bondingCurve", env), addr),
      enabled: enabled && !!addr,
      staleTime: 30_000,
    })),
  });

  const detailMap = useMemo(() => {
    const map = new Map<string, ForgeTokenDetail>();
    for (let i = 0; i < results.length; i++) {
      const data = results[i]?.data;
      const addr = tokenAddresses[i];
      if (data && addr) map.set(addr.toLowerCase(), data);
    }
    return map;
  }, [results, tokenAddresses]);

  const isLoading = results.some((r) => r.isLoading);

  return { detailMap, isLoading };
}
